/**
 * Shrink a photo on the phone, before it is ever uploaded.
 *
 * This is where the decision not to run `sharp` on the server pays off: the
 * upload is 15 kB instead of 4 MB, the server never decodes attacker-supplied
 * pixels, and there is no native dependency to build for a container that has
 * exactly one.
 *
 * WebP where the browser will, JPEG where it will not. Safari only learned
 * `canvas.toBlob('image/webp')` in 14 and — the part that catches people — an
 * older Safari does not fail: it silently hands back a PNG. So the type of the
 * blob is checked rather than assumed, and the server refuses PNG anyway.
 */

/** Both sides. Everything the app shows is square and at most 40 px. */
export const AVATAR_SIZE = 256;

export interface ResizedImage {
  blob: Blob;
  /** 'image/webp' or 'image/jpeg' — whatever actually came out. */
  type: string;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Dit bestand is geen foto.'));
    };
    image.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Centre-crop to a square and scale to 256×256.
 *
 * Cropping rather than squashing: a face stretched to fit is worse than a face
 * with its edges trimmed, and every place this appears is a circle anyway.
 */
export async function resizeForAvatar(file: File): Promise<ResizedImage> {
  const image = await loadImage(file);

  const side = Math.min(image.naturalWidth, image.naturalHeight);
  if (side === 0) throw new Error('Dit bestand is geen foto.');

  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Deze browser kan de foto niet verkleinen.');

  context.drawImage(
    image,
    (image.naturalWidth - side) / 2,
    (image.naturalHeight - side) / 2,
    side,
    side,
    0,
    0,
    AVATAR_SIZE,
    AVATAR_SIZE
  );

  // Ask for WebP; accept it only if that is genuinely what came back.
  const webp = await toBlob(canvas, 'image/webp', 0.86);
  if (webp && webp.type === 'image/webp') return { blob: webp, type: 'image/webp' };

  const jpeg = await toBlob(canvas, 'image/jpeg', 0.85);
  if (jpeg && jpeg.type === 'image/jpeg') return { blob: jpeg, type: 'image/jpeg' };

  throw new Error('Deze browser kan de foto niet omzetten.');
}
